import { notify, getApiErrorMessage } from '@/lib/toast';
import { useState, useEffect, Fragment, useCallback } from 'react';
import { RecordLink } from '@/components/records/RecordLink';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
// SSOT for the OFF-mode dummy classifier (T-283/T-297) — shared with the backend.
import { isOfflineDummyInvoiceId } from '@shared/types/fbr-offline';
import {
  Receipt,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  ShieldCheck,
  Settings,
  Eye,
  EyeOff,
  Save,
  WifiOff,
  TestTube2,
  Wifi,
  Lock,
  Key,
  Hash,
  Circle,
  FileText,
  Send,
  ChevronRight,
  Building2,
  User,
  Package,
  ExternalLink,
  Activity,
  Play,
  Trash2,
  AlertTriangle,
  Search,
  Filter,
  Pencil,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/ui/page-header';
import { TabBar } from '@/components/ui/tab-bar';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { Dialog } from 'primereact/dialog';
import api from '@/lib/api';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'primereact/dropdown';

// ─── Types ───────────────────────────────────────────────────────────────────

type FbrMode = 'OFF' | 'STAGING' | 'LIVE';
type ScenarioStatus = 'NOT_POSTED' | 'PENDING' | 'ACCEPTED' | 'FAILED';
type Tab = 'settings' | 'scenarios' | 'submissions' | 'api-logs' | 'products';

/**
 * FBR staging server limitations — these scenarios cannot pass in the sandbox
 * regardless of configuration. They are known gaps in FBR's staging environment.
 */
const STAGING_LIMITATION_SCENARIOS: Record<string, string> = {
  SN004: 'Ship breaking — FBR staging has no HS codes mapped to this sale type',
  SN011: 'Toll Manufacturing — scenario not registered in FBR staging DB',
  SN021: 'Cement/Concrete Block — no rate configuration in FBR staging',
  SN022: 'Potassium Chlorate — no rate configuration in FBR staging',
  SN023: 'CNG Sales — no rate configuration in FBR staging',
};

/** Intermittent staging limitations — code is correct but staging DB is incomplete */
const STAGING_INTERMITTENT_SCENARIOS = new Set(['SN015', 'SN020']);

interface FbrSettings {
  fbrEnvironment: FbrMode;
  fbrStagingApproved: boolean;
  fbrNtn: string;
  hasStagingToken: boolean;
  fbrStagingToken: string;
  hasLiveToken: boolean;
  fbrLiveToken: string;
}

interface ScenarioResult {
  scenarioId: string;
  description: string;
  status: ScenarioStatus;
  environment: string | null;
  fbrInvoiceId: string | null;
  invoiceRefNo: string | null;
  errorMessage: string | null;
  postedAt: string | null;
  saleId: string | null;
  fbrSubmissionId: string | null;
}

interface FbrDiInvoiceItem {
  hsCode: string;
  productDescription: string;
  rate: string;
  uoM: string;
  quantity: number;
  totalValues: number;
  valueSalesExcludingST: number;
  fixedNotifiedValueOrRetailPrice: number;
  salesTaxApplicable: number;
  salesTaxWithheldAtSource: number;
  extraTax: string;
  furtherTax: number;
  sroScheduleNo: string;
  fedPayable: number;
  discount: number;
  saleType: string;
  sroItemSerialNo: string;
}

interface FbrDiInvoicePayload {
  invoiceType: string;
  invoiceDate: string;
  sellerNTNCNIC: string;
  sellerBusinessName: string;
  sellerProvince: string;
  sellerAddress: string;
  buyerNTNCNIC: string;
  buyerBusinessName: string;
  buyerProvince: string;
  buyerAddress: string;
  buyerRegistrationType: string;
  invoiceRefNo: string;
  scenarioId: string;
  items: FbrDiInvoiceItem[];
}

interface ScenarioPreview {
  scenarioId: string;
  description: string;
  payload: FbrDiInvoicePayload;
}

type FbrDocumentKind = 'SALE_INVOICE' | 'CREDIT_NOTE' | 'CANCEL';

interface FbrSubmissionRow {
  id: string;
  kind: FbrDocumentKind;
  status: string;
  invoiceNumber: string;
  fbrInvoiceId?: string | null;
  attempts: number;
  createdAt: string;
  submittedAt?: string | null;
  updatedAt: string;
  errorMessage?: string | null;
  fieldSuggestions?: Array<{
    field: string;
    itemSku?: string;
    message?: string;
    suggestedValue?: unknown;
  }>;
  saleId: string;
  sale?: { invoiceNumber?: string };
  originalSubmission?: {
    submittedAt?: string | null;
    updatedAt: string;
    status: string;
    kind: string;
  } | null;
}

const FBR_CANCEL_WINDOW_HOURS = 72;

function kindDisplayLabel(kind: FbrDocumentKind, t: (key: string) => string): string {
  switch (kind) {
    case 'CREDIT_NOTE':
      return t('kindCreditNote');
    case 'CANCEL':
      return t('kindCancel');
    default:
      return t('kindSaleInvoice');
  }
}

function cancelWindowLabel(
  sub: FbrSubmissionRow,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string | null {
  if (sub.kind === 'CANCEL' && sub.status === 'INVALID') {
    return t('cancelWindowClosed');
  }
  if (sub.kind === 'SALE_INVOICE' && sub.status === 'ACCEPTED') {
    const acceptedAt = sub.submittedAt ?? sub.updatedAt;
    const hoursElapsed =
      (Date.now() - new Date(acceptedAt).getTime()) / (1000 * 60 * 60);
    const remaining = FBR_CANCEL_WINDOW_HOURS - hoursElapsed;
    if (remaining > 0) {
      return t('cancelWindowRemaining', { hours: Math.ceil(remaining) });
    }
    return t('cancelWindowClosed');
  }
  return null;
}

// ─── FBR Error Plain-English Guide ───────────────────────────────────────────

const FBR_ERROR_GUIDE: Record<string, { title: string; explain: string; fix: string }> = {
  '0052': {
    title: 'HS Code / Sale Type mismatch',
    explain: 'The HS code on one or more items does not match the sale type (scenario). FBR validates that each HS code is valid for the declared scenario.',
    fix: 'Open the product and verify the HS code. For Exempt goods (SN006) leave SRO fields empty. For standard goods (SN001/SN002) use a valid 18% HS code.',
  },
  '0099': {
    title: 'Invalid UOM for this HS Code',
    explain: 'The Unit of Measure (UOM) you sent is not allowed for the selected HS code. FBR maintains a list of valid UOMs per HS code.',
    fix: 'Edit the product and change the UOM. Use "Pcs" for individual items, "KGS" for weight-sold goods, "PKT" for packaged goods. Click "Verify with FBR" to check the valid UOM for the HS code.',
  },
  '0096': {
    title: 'Wrong UOM for Electricity (SN013)',
    explain: 'Electricity sales must use UOM = "KWH". Any other UOM is rejected by FBR.',
    fix: 'Set the product UOM to "KWH".',
  },
  '0001': {
    title: 'Invalid Seller NTN',
    explain: 'The seller NTN/CNIC is missing or not recognized by FBR.',
    fix: 'Go to FBR Settings and verify your NTN is a 7-digit number without dashes.',
  },
  '0002': {
    title: 'Invoice number already used',
    explain: 'FBR has already processed an invoice with this reference number.',
    fix: 'This invoice may have been submitted already. Check the FBR portal. If not found, void and re-create the sale.',
  },
  '0010': {
    title: 'Invalid invoice date',
    explain: 'The invoice date is in the future, too far in the past, or in the wrong format.',
    fix: 'Ensure the invoice date matches today\'s date. FBR rejects submissions older than a few days.',
  },
  '0020': {
    title: 'Invalid buyer registration type',
    explain: 'The buyer type does not match the scenario. SN001 requires a Registered buyer (NTN); SN002/SN026/SN027 require Unregistered.',
    fix: 'Check the customer NTN/CNIC. Registered = 7-digit NTN; Unregistered = 13-digit CNIC or empty.',
  },
  '0030': {
    title: 'Tax amount calculation error',
    explain: 'The sales tax amount in the invoice does not match what FBR calculates from the declared sale value and rate.',
    fix: 'Re-submit the invoice — the system will recalculate tax from live product data. If the product tax rate was recently changed, this fixes it.',
  },
  '0040': {
    title: 'SRO Schedule mismatch',
    explain: 'The SRO schedule number does not correspond to the HS code or rate provided.',
    fix: 'For SN006 (Exempt) items: leave SRO fields completely empty. For reduced-rate items: verify the SRO using the FBR reference API.',
  },
};

/** Return a rich error guide entry if the raw error message contains a known code */
function getFbrErrorGuide(errorMessage: string | null): (typeof FBR_ERROR_GUIDE)[string] | null {
  if (!errorMessage) return null;
  for (const [code, guide] of Object.entries(FBR_ERROR_GUIDE)) {
    if (errorMessage.includes(code)) return guide;
  }
  return null;
}



const submissionStatusConfig: Record<string, { icon: typeof CheckCircle; className: string; dot: string; label?: string }> = {
  ACCEPTED: { icon: CheckCircle, className: 'text-success-text bg-success/10 ring-success/30', dot: 'bg-success' },
  OFFLINE: { icon: Circle, className: 'text-muted-foreground bg-muted ring-border', dot: 'bg-muted-foreground', label: 'Offline' },
  REJECTED: { icon: XCircle, className: 'text-destructive-text bg-destructive/10 ring-destructive/30', dot: 'bg-destructive' },
  PENDING: { icon: Clock, className: 'text-warning-text bg-warning/10 ring-warning/30', dot: 'bg-warning' },
  RETRYING: { icon: RefreshCw, className: 'text-warning-text bg-warning/10 ring-warning/30', dot: 'bg-warning' },
  FAILED: { icon: XCircle, className: 'text-destructive-text bg-destructive/10 ring-destructive/30', dot: 'bg-destructive' },
  INVALID: { icon: AlertTriangle, className: 'text-warning-text bg-warning/10 ring-warning/30', dot: 'bg-warning' },
};

const scenarioStatusConfig: Record<ScenarioStatus, { label: string; icon: typeof CheckCircle; className: string; dot: string }> = {
  NOT_POSTED: { label: 'Not Posted', icon: Circle, className: 'text-muted-foreground bg-muted ring-border', dot: 'bg-muted-foreground' },
  PENDING: { label: 'Posting…', icon: Clock, className: 'text-warning-text bg-warning/10 ring-warning/30', dot: 'bg-warning animate-pulse' },
  ACCEPTED: { label: 'Posted ✓', icon: CheckCircle, className: 'text-success-text bg-success/10 ring-success/30', dot: 'bg-success' },
  FAILED: { label: 'Failed', icon: XCircle, className: 'text-destructive-text bg-destructive/10 ring-destructive/30', dot: 'bg-destructive' },
};

// ─── Reusable Badge components ─────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const cfg = submissionStatusConfig[status];
  if (!cfg) return <span className="text-xs text-muted-foreground">{status}</span>;
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset', cfg.className)}>
      <span className={cn('h-1.5 w-1.5 rounded-full', cfg.dot)} />
      {cfg.label ?? status}
    </span>
  );
}

function ScenarioBadge({ status }: { status: ScenarioStatus }) {
  const { t } = useTranslation('fbr');
  const cfg = scenarioStatusConfig[status];
  const labelKey: Record<ScenarioStatus, string> = {
    NOT_POSTED: t('scenarioNotPosted'),
    PENDING: t('scenarioPosting'),
    ACCEPTED: t('scenarioPosted'),
    FAILED: t('scenarioFailed'),
  };
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset transition-all duration-200', cfg.className)}>
      <span className={cn('h-1.5 w-1.5 rounded-full', cfg.dot)} />
      {labelKey[status]}
    </span>
  );
}

// ─── Skeleton loader ─────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <div className="flex items-center gap-4 px-4 py-3.5 border-b last:border-b-0">
      <div className="h-4 w-12 rounded-md bg-muted animate-pulse" />
      <div className="flex-1 space-y-1.5">
        <div className="h-3.5 w-3/4 rounded bg-muted animate-pulse" />
        <div className="h-3 w-1/3 rounded bg-muted/60 animate-pulse" />
      </div>
      <div className="h-6 w-20 rounded-full bg-muted animate-pulse" />
      <div className="h-7 w-16 rounded-md bg-muted animate-pulse" />
    </div>
  );
}

// ─── Mode pill ────────────────────────────────────────────────────────────────

const MODE_META: Record<FbrMode, { label: string; description: string; icon: typeof WifiOff; gradient: string; chip: string; glow: string; ring: string; bg: string; text: string }> = {
  OFF: {
    label: 'Off',
    description: 'Invoices generate a local dummy number — no API call is made.',
    icon: WifiOff,
    gradient: 'from-card to-card border border-border',
    chip: 'text-muted-foreground',
    glow: 'shadow-none',
    ring: 'ring-muted-foreground/40',
    bg: 'bg-muted',
    text: 'text-foreground',
  },
  STAGING: {
    label: 'Staging',
    description: 'Posts to the FBR sandbox. Use this until all 28 scenarios are approved.',
    icon: TestTube2,
    gradient: 'from-warning to-warning',
    chip: 'text-warning-foreground',
    glow: 'shadow-warning/25',
    ring: 'ring-warning/30',
    bg: 'bg-warning/10',
    text: 'text-warning-text',
  },
  LIVE: {
    label: 'Live',
    description: 'Posts to FBR production. Requires staging approval first.',
    icon: Wifi,
    gradient: 'from-success-solid to-success-solid',
    chip: 'text-success-solid-foreground',
    glow: 'shadow-success/25',
    ring: 'ring-success/30',
    bg: 'bg-success/10',
    text: 'text-success-text',
  },
};

// ─── Settings Tab ─────────────────────────────────────────────────────────────

function SettingsTab() {
  const { t } = useTranslation('fbr');
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();
  const canManage = hasPermission('fbr:manage') || hasPermission('fbr:*') || hasPermission('settings:update');

  const { data: settings, isLoading } = useQuery<FbrSettings>({
    queryKey: ['fbr-settings'],
    queryFn: async () => api.get('/tenant/fbr-settings').then((r) => r.data),
  });

  const [mode, setMode] = useState<FbrMode>('OFF');
  const [stagingToken, setStagingToken] = useState('');
  const [liveToken, setLiveToken] = useState('');
  const [ntn, setNtn] = useState('');
  const [showStagingToken, setShowStagingToken] = useState(false);
  const [showLiveToken, setShowLiveToken] = useState(false);
  const [stagingApproved, setStagingApproved] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (settings) {
      setMode(settings.fbrEnvironment ?? 'OFF');
      setNtn(settings.fbrNtn ?? '');
      setStagingApproved(settings.fbrStagingApproved ?? false);
    }
  }, [settings]);

  const save = useMutation({
    mutationFn: (payload: object) => api.patch('/tenant/fbr-settings', payload).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fbr-settings'] });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
    onError: (err) => notify.error(err, t('failedToSaveFbr')),
  });

  const canGoLive = settings?.fbrStagingApproved === true;

  const handleSave = () => {
    const payload: Record<string, unknown> = {
      fbrEnvironment: mode,
      fbrNtn: ntn,
      fbrStagingApproved: stagingApproved,
    };
    if (stagingToken) payload.fbrStagingToken = stagingToken;
    if (liveToken) payload.fbrLiveToken = liveToken;
    save.mutate(payload);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-36 rounded-xl border bg-card animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* ── Mode selector ── */}
      <div className="animate-fade-in-up stagger-1">
        <div className="mb-3 flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10">
            <Wifi className="h-3.5 w-3.5 text-primary" />
          </div>
          <h3 className="text-sm font-semibold">{t('postingMode')}</h3>
        </div>
        <div className="space-y-2.5">
          {(['OFF', 'STAGING', 'LIVE'] as FbrMode[]).map((m) => {
            const meta = MODE_META[m];
            const Icon = meta.icon;
            const isSelected = mode === m;
            const isDisabled = m === 'LIVE' && !canGoLive;
            return (
              <button
                key={m}
                disabled={isDisabled}
                onClick={() => !isDisabled && setMode(m)}
                className={cn(
                  'group relative w-full overflow-hidden rounded-xl border-2 p-4 text-start transition-all duration-200',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  isSelected
                    ? cn('border-transparent', meta.bg, meta.ring, 'ring-2')
                    : 'border-border bg-card hover:border-border/60 hover:bg-accent/30',
                  isDisabled && 'cursor-not-allowed opacity-50',
                )}
              >
                <div className="flex items-start gap-3.5">
                  <div className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br shadow-md transition-all duration-200',
                    meta.chip,
                    meta.gradient,
                    meta.glow,
                    isSelected ? 'shadow-lg scale-105' : 'shadow-sm',
                  )}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={cn('text-sm font-semibold', isSelected ? meta.text : 'text-foreground')}>
                        {meta.label}
                      </p>
                      {isSelected && (
                        <span className={cn(
                          'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs font-medium',
                          meta.bg, meta.text,
                        )}>
                          {t('activeLabel')}
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">{meta.description}</p>
                    {m === 'LIVE' && !canGoLive && (
                      <div className="mt-1.5 flex items-center gap-1 text-xs text-warning-text">
                        <Lock className="h-3 w-3" />
                        {t('stagingLockHint')}
                      </div>
                    )}
                  </div>
                  <div className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-200',
                    isSelected ? 'border-current bg-current' : 'border-muted-foreground/30',
                  )}>
                    {isSelected && <div className="h-2 w-2 rounded-full bg-background" />}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Bearer Tokens ── */}
      <div className="animate-fade-in-up stagger-2">
        <div className="mb-3 flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10">
            <Key className="h-3.5 w-3.5 text-primary" />
          </div>
          <h3 className="text-sm font-semibold">{t('fbrBearerTokens')}</h3>
        </div>
        <div className="rounded-xl border bg-card p-4 space-y-4">
          <p className="text-xs text-muted-foreground leading-relaxed">
            FBR issues <strong>separate</strong> tokens for staging (sandbox) and production (live).
            Paste each one in the appropriate field below. Leave blank to keep the existing token.
          </p>

          {/* Staging token */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-warning-text">{t('stagingTokenLabel')}</Label>
              {settings?.hasStagingToken && (
                <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning-text ring-1 ring-inset ring-warning/30">
                  <span className="h-1.5 w-1.5 rounded-full bg-warning" />
                  {t('tokenSet')}
                </span>
              )}
            </div>
            <div className="relative">
              <Input
                type={showStagingToken ? 'text' : 'password'}
                placeholder={settings?.hasStagingToken ? '••••••••  (token already set)' : 'Paste FBR sandbox/staging token here'}
                value={stagingToken}
                onChange={(e) => setStagingToken(e.target.value)}
                className="pe-11 font-mono text-sm h-11"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
              <button
                type="button"
                onClick={() => setShowStagingToken((v) => !v)}
                className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                aria-label={showStagingToken ? t('hideToken') : t('showToken')}
              >
                {showStagingToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {settings?.hasStagingToken && (
              <p className="text-xs font-mono text-muted-foreground tracking-wider">
                Current: {settings.fbrStagingToken}
              </p>
            )}
          </div>

          {/* Live token */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-success-text">{t('productionTokenLabel')}</Label>
              {settings?.hasLiveToken && (
                <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success-text ring-1 ring-inset ring-success/30">
                  <span className="h-1.5 w-1.5 rounded-full bg-success" />
                  {t('tokenSet')}
                </span>
              )}
            </div>
            <div className="relative">
              <Input
                type={showLiveToken ? 'text' : 'password'}
                placeholder={settings?.hasLiveToken ? '••••••••  (token already set)' : 'Paste FBR production/live token here'}
                value={liveToken}
                onChange={(e) => setLiveToken(e.target.value)}
                className="pe-11 font-mono text-sm h-11"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
              <button
                type="button"
                onClick={() => setShowLiveToken((v) => !v)}
                className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                aria-label={showLiveToken ? t('hideToken') : t('showToken')}
              >
                {showLiveToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {settings?.hasLiveToken && (
              <p className="text-xs font-mono text-muted-foreground tracking-wider">
                Current: {settings.fbrLiveToken}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── NTN ── */}
      <div className="animate-fade-in-up stagger-3">
        <div className="mb-3 flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10">
            <Hash className="h-3.5 w-3.5 text-primary" />
          </div>
          <h3 className="text-sm font-semibold">{t('sellerNtn')}</h3>
        </div>
        <div className="rounded-xl border bg-card p-4 space-y-3">
          <p className="text-xs text-muted-foreground">
            Your 13-digit tax registration number — included in every DI invoice payload sent to FBR.
          </p>
          <Input
            dir="ltr"
            placeholder="0000000000000"
            value={ntn}
            onChange={(e) => setNtn(e.target.value.replace(/\D/g, '').slice(0, 13))}
            maxLength={13}
            className="font-mono text-sm h-11 tracking-widest"
            inputMode="numeric"
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">{t('ntnDigitsRequired')}</p>
            <p className={cn('text-xs font-mono font-medium tabular-nums', ntn.length === 13 ? 'text-success-text' : 'text-muted-foreground')}>
              {ntn.length}/13
            </p>
          </div>
        </div>
      </div>

      {/* ── Staging Approved ── */}
      <div className="animate-fade-in-up stagger-4">
        <div className="mb-3 flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" />
          </div>
          <h3 className="text-sm font-semibold">{t('stagingApprovedTitle')}</h3>
        </div>
        <div className={cn(
          'rounded-xl border p-4 transition-all duration-300',
          stagingApproved ? 'bg-success/5 border-success/30' : 'bg-card',
        )}>
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <p className="text-sm font-medium">
                {stagingApproved ? (
                  <span className="text-success-text">{t('stagingApprovedYes')}</span>
                ) : (
                  t('stagingApprovedNo')
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('stagingApprovedHint')}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={stagingApproved}
              onClick={() => setStagingApproved((v) => !v)}
              className={cn(
                'relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                stagingApproved ? 'bg-success-solid' : 'bg-muted-foreground/25',
              )}
            >
              <span className={cn(
                'pointer-events-none inline-block h-5 w-5 rounded-full bg-background shadow-md ring-0 transition-transform duration-200 ease-in-out',
                stagingApproved ? 'translate-x-5' : 'translate-x-0',
              )} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Save ── */}
      <div className="animate-fade-in-up stagger-5 flex flex-col gap-3">
        {!canManage && (
          <p className="text-sm text-muted-foreground text-center py-8">
            {t('fbrPermissionRequired')}
          </p>
        )}
        {canManage && (
          <Button
            onClick={handleSave}
            disabled={save.isPending}
            className={cn(
              'h-11 w-full text-sm font-semibold transition-all duration-200',
              saveSuccess && 'bg-success-solid text-success-solid-foreground hover:bg-success-solid',
            )}
          >
            {save.isPending ? (
              <>
                <Loader2 className="me-2 h-4 w-4 animate-spin" />
                {t('savingLabel')}
              </>
            ) : saveSuccess ? (
              <>
                <CheckCircle className="me-2 h-4 w-4" />
                {t('savedSuccess')}
              </>
            ) : (
              <>
                <Save className="me-2 h-4 w-4" />
                {t('saveSettingsBtn')}
              </>
            )}
          </Button>
        )}

        {save.isError && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive-text">
            <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{getApiErrorMessage(save.error, 'Failed to save settings. Please try again.')}</span>
          </div>
        )}
      </div>

      {/* ── FBR Reference Data Sync (super-admin only) ── */}
      <ReferenceDataSyncCard stagingToken={settings?.fbrStagingToken} />
    </div>
  );
}

/** Super-admin only card to sync FBR reference data (SRO schedules, rates, UOMs, provinces) */
function ReferenceDataSyncCard({ stagingToken }: { stagingToken?: string }) {
  const { t } = useTranslation('fbr');
  const { user } = useAuthStore();
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ success: boolean; message: string } | null>(null);

  if (!user?.isSuperAdmin) return null;

  const handleSync = async () => {
    if (!stagingToken) {
      setSyncResult({ success: false, message: 'No FBR staging token configured. Save a staging token first.' });
      return;
    }
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await api.post('/platform/fbr-reference/sync', { token: stagingToken });
      const d = res.data;
      setSyncResult({
        success: true,
        message: `Synced: ${d.uomCount} UOMs, ${d.provinceCount} provinces, ${d.rateCount} rates, ${d.sroCount} SRO schedules`,
      });
    } catch (err: unknown) {
      setSyncResult({ success: false, message: getApiErrorMessage(err, 'Sync failed') });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="rounded-xl border bg-card p-5 space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-info/15">
          <RefreshCw className="h-4 w-4 text-info-text" />
        </div>
        <div>
          <p className="text-sm font-semibold">{t('refDataSyncTitle')}</p>
          <p className="text-xs text-muted-foreground">Super-admin only · Syncs SRO schedules, rates, UOMs from FBR APIs</p>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        This populates the global FBR lookup tables used by all dropdowns (SRO schedules, UOMs).
        Run once after initial setup and whenever FBR updates their reference data.
        Uses the staging token configured above.
      </p>
      <Button
        variant="outline"
        size="sm"
        onClick={handleSync}
        disabled={syncing}
        className="w-full"
      >
        {syncing ? (
          <><Loader2 className="me-2 h-3.5 w-3.5 animate-spin" />{t('syncingLabel')}</>
        ) : (
          <><RefreshCw className="me-2 h-3.5 w-3.5" />{t('syncFbrData')}</>
        )}
      </Button>
      {syncResult && (
        <div className={cn(
          'flex items-start gap-2 rounded-lg border p-3 text-xs',
          syncResult.success
            ? 'border-success/30 bg-success/10 text-success-text'
            : 'border-destructive/30 bg-destructive/10 text-destructive-text',
        )}>
          {syncResult.success
            ? <CheckCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            : <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
          <span>{syncResult.message}</span>
        </div>
      )}
    </div>
  );
}

// ─── Invoice Preview Modal ────────────────────────────────────────────────────

function InvoicePreviewModal({
  preview,
  onClose,
  onPost,
  isPosting,
  postResult,
}: {
  preview: ScenarioPreview;
  onClose: () => void;
  onPost: () => void;
  isPosting: boolean;
  postResult?: { success: boolean; fbrInvoiceId?: string | null; errorMessage?: string | null } | null;
}) {
  const { t } = useTranslation('fbr');
  const p = preview.payload;
  const totalNet = p.items.reduce((s, i) => s + i.valueSalesExcludingST, 0);
  const totalTax = p.items.reduce((s, i) => s + i.salesTaxApplicable, 0);
  const grandTotal = p.items.reduce((s, i) => s + i.totalValues, 0);

  const dialogHeader = (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
        <FileText className="h-4 w-4 text-primary" />
      </div>
      <div>
        <div className="flex items-center gap-2">
          <span className="text-base font-semibold">{t('invoicePreview')}</span>
          <span className="font-mono text-xs font-bold text-primary-text bg-primary/10 px-1.5 py-0.5 rounded">
            {preview.scenarioId}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">{preview.description}</p>
      </div>
    </div>
  );

  const dialogFooter = (
    <div className="flex items-center justify-between gap-3 w-full">
      <Button variant="outline" onClick={onClose} disabled={isPosting} className="h-10">
        {postResult?.success ? t('closeBtn') : t('cancelBtn')}
      </Button>
      {!postResult?.success && (
        <Button
          onClick={onPost}
          disabled={isPosting}
          className="h-10 min-w-36"
        >
          {isPosting ? (
            <><Loader2 className="me-2 h-4 w-4 animate-spin" />{t('postingLabel')}</>
          ) : (
            <><Send className="me-2 h-4 w-4" />{t('postToFbr')}</>
          )}
        </Button>
      )}
    </div>
  );

  return (
    <Dialog
      visible={true}
      onHide={!isPosting ? onClose : () => {}}
      header={dialogHeader}
      footer={dialogFooter}
      style={{ width: '42rem' }}
      draggable={false}
      resizable={false}
    >
      <div className="space-y-5">
        {/* Success / Error result */}
        {postResult && (
          <div className={cn(
            'flex items-start gap-3 rounded-xl border p-4',
            postResult.success
              ? 'border-success/30 bg-success/10'
              : 'border-destructive/30 bg-destructive/10',
          )}>
            {postResult.success ? (
              <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-success-text" />
            ) : (
              <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive-text" />
            )}
            <div>
              <p className={cn('text-sm font-semibold', postResult.success ? 'text-success-text' : 'text-destructive-text')}>
                {postResult.success ? t('postedSuccess') : t('postingFailed')}
              </p>
              {postResult.fbrInvoiceId && (
                <p className="mt-1 font-mono text-xs text-success-text">
                  FBR Invoice: {postResult.fbrInvoiceId}
                </p>
              )}
              {postResult.errorMessage && (
                <p className="mt-1 text-xs text-destructive-text">{postResult.errorMessage}</p>
              )}
            </div>
          </div>
        )}

        {/* Invoice meta */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border bg-muted/30 p-3.5 space-y-0.5">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Building2 className="h-3 w-3" /> {t('sellerLabel')}
            </p>
            <p className="text-sm font-semibold">{p.sellerBusinessName}</p>
            <p className="text-xs text-muted-foreground font-mono">NTN: {p.sellerNTNCNIC}</p>
            <p className="text-xs text-muted-foreground">{p.sellerAddress}</p>
            <p className="text-xs text-muted-foreground">{p.sellerProvince}</p>
          </div>
          <div className="rounded-xl border bg-muted/30 p-3.5 space-y-0.5">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <User className="h-3 w-3" /> {t('buyerLabel')}
            </p>
            <p className="text-sm font-semibold">{p.buyerBusinessName}</p>
            <p className="text-xs text-muted-foreground font-mono">
              {p.buyerRegistrationType} {p.buyerNTNCNIC ? `· ${p.buyerNTNCNIC}` : ''}
            </p>
            <p className="text-xs text-muted-foreground">{p.buyerAddress}</p>
            <p className="text-xs text-muted-foreground">{p.buyerProvince}</p>
          </div>
        </div>

        <div className="flex items-center gap-4 rounded-xl border bg-muted/30 px-4 py-3 text-xs text-muted-foreground">
          <span><strong className="text-foreground">{t('typeLabel')}:</strong> {p.invoiceType}</span>
          <span><strong className="text-foreground">{t('dateLabel')}:</strong> {p.invoiceDate}</span>
          <span><strong className="text-foreground">{t('refLabel')}:</strong> <span className="font-mono">{p.invoiceRefNo}</span></span>
        </div>

        {/* Line items */}
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Package className="h-3 w-3" /> {t('itemsLabel')}
          </p>
          <div className="rounded-xl border overflow-hidden">
            <Table className="text-xs">
              <THead>
                <Tr hover={false}>
                  <Th className="px-3 py-2">{t('descriptionCol')}</Th>
                  <Th className="px-3 py-2 hidden sm:table-cell">{t('hsCodeCol')}</Th>
                  <Th className="px-3 py-2 hidden sm:table-cell">{t('uomCol')}</Th>
                  <Th className="px-3 py-2 text-end">{t('rateCol')}</Th>
                  <Th className="px-3 py-2 text-end">{t('qtyCol')}</Th>
                  <Th className="px-3 py-2 text-end">{t('netCol')}</Th>
                  <Th className="px-3 py-2 text-end">{t('taxCol')}</Th>
                  <Th className="px-3 py-2 text-end">{t('totalCol')}</Th>
                </Tr>
              </THead>
              <TBody>
                {p.items.map((item, i) => (
                  <Tr key={i} hover={false}>
                    <Td className="px-3 py-2.5 font-medium">{item.productDescription}</Td>
                    <Td className="px-3 py-2.5 font-mono text-muted-foreground hidden sm:table-cell">{item.hsCode}</Td>
                    <Td className="px-3 py-2.5 text-muted-foreground hidden sm:table-cell">{item.uoM}</Td>
                    <Td className="px-3 py-2.5 text-end">{item.rate}</Td>
                    <Td className="px-3 py-2.5 text-end tabular-nums">{item.quantity}</Td>
                    <Td className="px-3 py-2.5 text-end tabular-nums">{formatCurrency(item.valueSalesExcludingST)}</Td>
                    <Td className="px-3 py-2.5 text-end tabular-nums">{formatCurrency(item.salesTaxApplicable)}</Td>
                    <Td className="px-3 py-2.5 text-end tabular-nums font-semibold">{formatCurrency(item.totalValues)}</Td>
                  </Tr>
                ))}
              </TBody>
              <tfoot>
                <Tr hover={false} className="bg-muted/40 !border-t">
                  <Td colSpan={5} className="px-3 py-2 text-end text-xs font-semibold text-muted-foreground hidden sm:table-cell">{t('totalsLabel')}</Td>
                  <Td colSpan={5} className="px-3 py-2 text-end text-xs font-semibold text-muted-foreground sm:hidden">{t('totalsLabel')}</Td>
                  <Td className="px-3 py-2 text-end tabular-nums font-semibold hidden sm:table-cell">{formatCurrency(totalNet)}</Td>
                  <Td className="px-3 py-2 text-end tabular-nums font-semibold hidden sm:table-cell">{formatCurrency(totalTax)}</Td>
                  <Td className="px-3 py-2 text-end tabular-nums font-bold">{formatCurrency(grandTotal)}</Td>
                </Tr>
              </tfoot>
            </Table>
          </div>
          {p.items[0]?.saleType && (
            <p className="mt-1.5 text-xs text-muted-foreground">
              Sale Type: <span className="font-medium text-foreground">{p.items[0].saleType}</span>
            </p>
          )}
        </div>
      </div>
    </Dialog>
  );
}

// ─── Scenario Checklist Tab ───────────────────────────────────────────────────

function ScenarioChecklistTab() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('fbr');
  const { hasPermission } = useAuthStore();
  const canManage = hasPermission('fbr:manage') || hasPermission('fbr:*');
  const [activePreview, setActivePreview] = useState<ScenarioPreview | null>(null);
  const [postResult, setPostResult] = useState<{ success: boolean; fbrInvoiceId?: string | null; errorMessage?: string | null } | null>(null);
  const [loadingPreviewId, setLoadingPreviewId] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);

  const { data: scenarios = [], isLoading, refetch } = useQuery<ScenarioResult[]>({
    queryKey: ['fbr-scenario-tests'],
    queryFn: async () => api.get('/fbr/scenario-tests').then((r) => r.data),
    refetchInterval: (query) => {
      const hasPending = (query.state.data ?? []).some((s: ScenarioResult) => s.status === 'PENDING');
      return hasPending ? 3000 : false;
    },
  });

  const postScenario = useMutation({
    mutationFn: (scenarioId: string) =>
      api.post(`/fbr/scenarios/${scenarioId}/post`).then((r) => r.data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['fbr-scenario-tests'] });
      setPostResult({
        success: data.status === 'ACCEPTED',
        fbrInvoiceId: data.fbrInvoiceId,
        errorMessage: data.errorMessage,
      });
    },
    onError: (error: unknown) => {
      setPostResult({
        success: false,
        errorMessage: getApiErrorMessage(error, 'Posting failed'),
      });
    },
  });

  const clearScenarios = useMutation({
    mutationFn: () => api.post('/fbr/scenarios/clear').then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fbr-scenario-tests'] });
      setConfirmClear(false);
    },
  });

  // Submit all scenarios sequentially by calling each one individually
  // (gives real-time progress visible via the auto-refresh)
  const [isSubmittingAll, setIsSubmittingAll] = useState(false);
  const handleSubmitAll = async () => {
    if (!canManage || isSubmittingAll) return;
    setIsSubmittingAll(true);
    const all = scenarios.map((s) => s.scenarioId);
    setBulkProgress({ done: 0, total: all.length });
    for (let i = 0; i < all.length; i++) {
      try {
        await api.post(`/fbr/scenarios/${all[i]}/post`);
      } catch {
        // continue with next even if one fails
      }
      setBulkProgress({ done: i + 1, total: all.length });
      queryClient.invalidateQueries({ queryKey: ['fbr-scenario-tests'] });
    }
    setIsSubmittingAll(false);
    setBulkProgress(null);
    queryClient.invalidateQueries({ queryKey: ['fbr-scenario-tests'] });
  };

  const handleCreateInvoice = async (scenarioId: string) => {
    setLoadingPreviewId(scenarioId);
    setPostResult(null);
    try {
      const data: ScenarioPreview = await api
        .get(`/fbr/scenarios/${scenarioId}/preview`)
        .then((r) => r.data);
      setActivePreview(data);
    } finally {
      setLoadingPreviewId(null);
    }
  };

  const handleCloseModal = () => {
    if (postScenario.isPending) return;
    setActivePreview(null);
    setPostResult(null);
  };

  const accepted = scenarios.filter((s) => s.status === 'ACCEPTED').length;
  const failed = scenarios.filter((s) => s.status === 'FAILED').length;
  const total = scenarios.length;
  const allPosted = total > 0 && accepted === total;
  const progress = total > 0 ? Math.round((accepted / total) * 100) : 0;

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 animate-fade-in-up stagger-1">
        {[
          { label: t('postedStat'), value: accepted, color: 'text-success-text', dot: 'bg-success' },
          { label: t('failedStat'), value: failed, color: 'text-destructive-text', dot: 'bg-destructive' },
          { label: t('remainingStat'), value: total - accepted - failed, color: 'text-muted-foreground', dot: 'bg-muted-foreground' },
        ].map(({ label, value, color, dot }) => (
          <div key={label} className="rounded-xl border bg-card p-4">
            <div className="flex items-center gap-2 mb-1">
              <span className={cn('h-2 w-2 rounded-full', dot)} />
              <span className="text-xs text-muted-foreground font-medium">{label}</span>
            </div>
            <p className={cn('text-2xl font-bold tabular-nums', color)}>{value}</p>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <div className="rounded-xl border bg-card p-4 animate-fade-in-up stagger-2">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-muted-foreground">{t('scenariosPostedLabel')}</span>
          <span className="text-xs font-semibold tabular-nums">{accepted}/{total}</span>
        </div>
        <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-700 ease-out',
              allPosted
                ? 'bg-gradient-to-r from-success to-success'
                : 'bg-gradient-to-r from-primary to-primary',
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
        {allPosted && (
          <div className="mt-3 flex items-center gap-2 text-sm font-medium text-success-text">
            <ShieldCheck className="h-4 w-4" />
            {t('allPostedMsg', { count: total })}
          </div>
        )}
      </div>

      {/* Header row */}
      <div className="flex items-center justify-between gap-3 animate-fade-in-up stagger-2">
        <p className="text-sm text-muted-foreground">
          {t('clickCreateInvoiceHint')}
        </p>
        <div className="flex items-center gap-2">
          {canManage && (
            <>
              {/* Clear Results */}
              {!confirmClear ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs text-destructive-text border-destructive/30 hover:border-destructive/40 hover:bg-destructive/10"
                  onClick={() => setConfirmClear(true)}
                  disabled={isSubmittingAll || scenarios.length === 0}
                >
                  <Trash2 className="me-1.5 h-3 w-3" />
                  {t('clearResults')}
                </Button>
              ) : (
                <div className="flex items-center gap-1.5 rounded-lg border border-destructive/30 bg-destructive/10 px-2.5 py-1">
                  <span className="text-xs text-destructive-text font-medium">{t('deleteAllConfirm')}</span>
                  <button
                    className="text-xs text-destructive-text font-semibold hover:underline"
                    onClick={() => clearScenarios.mutate()}
                    disabled={clearScenarios.isPending}
                  >
                    {clearScenarios.isPending ? t('clearingLabel') : t('yesClear')}
                  </button>
                  <button
                    className="text-xs text-muted-foreground hover:underline"
                    onClick={() => setConfirmClear(false)}
                  >
                    {t('cancelBtn')}
                  </button>
                </div>
              )}

              {/* Submit All */}
              <Button
                size="sm"
                className="h-8 text-xs"
                onClick={handleSubmitAll}
                disabled={isSubmittingAll || total === 0}
              >
                {isSubmittingAll ? (
                  <>
                    <Loader2 className="me-1.5 h-3 w-3 animate-spin" />
                    {bulkProgress ? `${bulkProgress.done}/${bulkProgress.total}` : t('submittingLabel')}
                  </>
                ) : (
                  <>
                    <Play className="me-1.5 h-3 w-3" />
                    {t('submitAll')}
                  </>
                )}
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={() => refetch()} className="h-8 text-xs" disabled={isSubmittingAll}>
            <RefreshCw className="me-1.5 h-3 w-3" />
            {t('refresh')}
          </Button>
        </div>
      </div>

      {/* Bulk submit progress bar */}
      {isSubmittingAll && bulkProgress && (
        <div className="rounded-xl border bg-card p-3.5 animate-fade-in-up">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-muted-foreground">{t('submittingAllScenarios')}</span>
            <span className="text-xs font-semibold tabular-nums">{bulkProgress.done}/{bulkProgress.total}</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-primary to-primary transition-all duration-300"
              style={{ width: `${Math.round((bulkProgress.done / bulkProgress.total) * 100)}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Each scenario is submitted sequentially — this may take 60–90 seconds for all 28.</p>
        </div>
      )}

      {/* Checklist */}
      <Card className="overflow-hidden animate-fade-in-up stagger-3">
        <div className="divide-y">
          {isLoading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-4">
                  <div className="h-5 w-5 rounded-full bg-muted animate-pulse" />
                  <div className="h-4 w-14 rounded bg-muted animate-pulse" />
                  <div className="flex-1 h-4 rounded bg-muted animate-pulse" />
                  <div className="h-6 w-20 rounded-full bg-muted animate-pulse" />
                  <div className="h-8 w-28 rounded-lg bg-muted animate-pulse" />
                </div>
              ))
            : scenarios.map((scenario, idx) => {
                const isAccepted = scenario.status === 'ACCEPTED';
                const isFailed = scenario.status === 'FAILED';
                const isPending = scenario.status === 'PENDING';
                const isLoadingThis = loadingPreviewId === scenario.scenarioId;

                return (
                  <div
                    key={scenario.scenarioId}
                    className={cn(
                      'flex items-center gap-4 px-5 py-4 transition-colors duration-150',
                      isAccepted && 'bg-success/5',
                      isFailed && 'bg-destructive/5',
                      isPending && 'bg-warning/5',
                    )}
                    style={{ animationDelay: `${idx * 25}ms` }}
                  >
                    {/* Checkbox indicator */}
                    <div className={cn(
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-300',
                      isAccepted
                        ? 'border-success-solid bg-success-solid'
                        : isFailed
                          ? 'border-destructive'
                          : 'border-muted-foreground/30',
                    )}>
                      {isAccepted && <CheckCircle className="h-3.5 w-3.5 text-success-solid-foreground" />}
                      {isFailed && <XCircle className="h-3.5 w-3.5 text-destructive-text" />}
                      {isPending && <Loader2 className="h-3 w-3 text-warning-text animate-spin" />}
                    </div>

                    {/* Scenario code */}
                    <span className="font-mono text-xs font-bold text-primary-text bg-primary/10 px-1.5 py-0.5 rounded shrink-0">
                      {scenario.scenarioId}
                    </span>

                    {/* Description + meta */}
                    <div className="flex-1 min-w-0">
                      <p className={cn('text-sm font-medium leading-snug', isAccepted && 'text-muted-foreground line-through decoration-success/60')}>
                        {scenario.description}
                      </p>
                      {/* Our invoice reference number */}
                      {scenario.invoiceRefNo && (
                        <p className="mt-0.5 font-mono text-xs text-muted-foreground/80">
                          Ref: {scenario.invoiceRefNo}
                        </p>
                      )}
                      {/* FBR-assigned invoice number (only on success) */}
                      {scenario.fbrInvoiceId && (
                        <p className="mt-0.5 font-mono text-xs text-success-text">
                          FBR: {scenario.fbrInvoiceId}
                        </p>
                      )}
                      {scenario.errorMessage && (
                        <p className="mt-0.5 text-xs text-destructive-text line-clamp-1" title={scenario.errorMessage}>
                          {scenario.errorMessage}
                        </p>
                      )}
                      {scenario.postedAt && !scenario.errorMessage && (
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {formatDate(scenario.postedAt)}
                        </p>
                      )}
                      {/* Staging limitation notice */}
                      {STAGING_LIMITATION_SCENARIOS[scenario.scenarioId] && (
                        <p className="mt-0.5 flex items-center gap-1 text-xs text-warning-text">
                          <AlertTriangle className="h-3 w-3 shrink-0" />
                          <span>Staging limitation — {STAGING_LIMITATION_SCENARIOS[scenario.scenarioId]}</span>
                        </p>
                      )}
                      {STAGING_INTERMITTENT_SCENARIOS.has(scenario.scenarioId) && !isAccepted && (
                        <p className="mt-0.5 flex items-center gap-1 text-xs text-warning-text/80">
                          <AlertTriangle className="h-3 w-3 shrink-0" />
                          <span>FBR staging data is intermittent for this scenario — retry may succeed</span>
                        </p>
                      )}
                    </div>

                    {/* Status badge */}
                    <ScenarioBadge status={scenario.status} />

                    {/* Action button */}
                    {canManage && (
                      <Button
                        variant={isAccepted ? 'ghost' : 'outline'}
                        size="sm"
                        className={cn(
                          'h-8 shrink-0 text-xs gap-1.5',
                          isAccepted && 'text-muted-foreground',
                        )}
                        disabled={isPending || isLoadingThis}
                        onClick={() => handleCreateInvoice(scenario.scenarioId)}
                      >
                        {isLoadingThis ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <>
                            <FileText className="h-3 w-3" />
                            {isAccepted ? t('rePost') : isFailed ? t('retryLabel') : t('createInvoiceBtn')}
                            <ChevronRight className="h-3 w-3 opacity-60" />
                          </>
                        )}
                      </Button>
                    )}

                    {/* View generated invoice link */}
                    {scenario.saleId && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 shrink-0 text-xs gap-1.5 text-primary-text"
                        onClick={() => window.open('/sales', '_blank')}
                        title={t('viewSalesTip')}
                      >
                        <ExternalLink className="h-3 w-3" />
                        <span className="hidden sm:inline">{t('salesLink')}</span>
                      </Button>
                    )}
                  </div>
                );
              })}
        </div>
      </Card>

      {/* Preview modal */}
      {activePreview && (
        <InvoicePreviewModal
          preview={activePreview}
          onClose={handleCloseModal}
          onPost={() => postScenario.mutate(activePreview.scenarioId)}
          isPosting={postScenario.isPending}
          postResult={postResult}
        />
      )}
    </div>
  );
}

// ─── Submissions Tab ──────────────────────────────────────────────────────────

function SubmissionsTab() {
  const [page] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { t } = useTranslation('fbr');
  const handleFbrUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['fbr-submissions'] });
  }, [queryClient]);
  useSocketEvent('sale.created', handleFbrUpdate);
  useSocketEvent('sale.voided', handleFbrUpdate);
  const { hasPermission } = useAuthStore();
  const canApplyFix = hasPermission('inventory:update') || hasPermission('inventory:*');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['fbr-submissions', page, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (statusFilter) params.set('status', statusFilter);
      return api.get(`/fbr/submissions?${params}`).then((r) => r.data);
    },
  });

  // Action Required total — fetched independently so the badge is accurate even
  // when the operator is viewing a different filter.
  const { data: actionData } = useQuery({
    queryKey: ['fbr-submissions', 'action-required-count'],
    queryFn: async () =>
      api.get('/fbr/submissions?status=ACTION_REQUIRED&limit=1').then((r) => r.data),
  });
  const actionRequiredTotal: number = actionData?.total ?? 0;

  const applyFixMutation = useMutation({
    mutationFn: (submissionId: string) => api.post(`/fbr/submissions/${submissionId}/apply-suggestions`).then((r) => r.data),
    onSuccess: (result) => {
      toast.success(t('fixAppliedSuccess', { count: result.updated ?? 0 }));
      queryClient.invalidateQueries({ queryKey: ['fbr-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (err: unknown) => {
      toast.error(getApiErrorMessage(err, t('failedToApplyFixes')));
    },
  });

  const { data: statsData } = useQuery({
    queryKey: ['fbr-submissions', 'stats'],
    queryFn: async () => api.get('/fbr/submissions/stats').then((r) => r.data),
  });

  const submissions: FbrSubmissionRow[] = data?.data ?? [];
  const countByStatus = (s: string) => {
    switch (s) {
      case 'ACCEPTED': return statsData?.accepted ?? 0;
      case 'PENDING': return statsData?.pending ?? 0;
      case 'RETRYING': return 0;
      case 'REJECTED': return statsData?.rejected ?? 0;
      case 'FAILED': return statsData?.failed ?? 0;
      case 'INVALID': return statsData?.invalid ?? 0;
      default: return submissions.filter((x) => x.status === s).length;
    }
  };

  return (
    <div className="space-y-5">
      {/* Action Required banner — surfaces permanent failures that need operator intervention */}
      {actionRequiredTotal > 0 && statusFilter !== 'ACTION_REQUIRED' && (
        <button
          type="button"
          onClick={() => setStatusFilter('ACTION_REQUIRED')}
          className="flex w-full items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-start transition-colors hover:bg-destructive/15 animate-fade-in-up"
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-destructive to-destructive text-destructive-foreground shadow-md shadow-destructive/20">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-destructive-text">
              {t('actionRequiredTitle', { count: actionRequiredTotal })}
            </p>
            <p className="text-xs text-destructive-text/80">{t('actionRequiredHint')}</p>
          </div>
          <ChevronRight className="h-4 w-4 text-destructive-text" />
        </button>
      )}

      {/* Stats — clickable to filter the table */}
      <div className="grid grid-cols-4 gap-3 animate-fade-in-up stagger-1">
        {[
          { label: t('statsAccepted'), count: countByStatus('ACCEPTED'), icon: CheckCircle, gradient: 'from-success-solid to-success-solid', fg: 'text-success-solid-foreground', glow: 'shadow-success/20', filter: 'ACCEPTED' },
          { label: t('statsPending'), count: countByStatus('PENDING') + countByStatus('RETRYING'), icon: Clock, gradient: 'from-warning to-warning', fg: 'text-warning-foreground', glow: 'shadow-warning/20', filter: 'PENDING' },
          { label: t('failedStat'), count: countByStatus('REJECTED') + countByStatus('FAILED'), icon: XCircle, gradient: 'from-destructive to-destructive', fg: 'text-destructive-foreground', glow: 'shadow-destructive/20', filter: 'ACTION_REQUIRED' },
          { label: t('invalidStat'), count: countByStatus('INVALID'), icon: AlertTriangle, gradient: 'from-warning to-warning', fg: 'text-warning-foreground', glow: 'shadow-warning/20', filter: 'INVALID' },
        ].map(({ label, count, icon: Icon, gradient, fg, glow, filter }) => (
          <button
            type="button"
            key={label}
            onClick={() => setStatusFilter((prev) => (prev === filter ? '' : filter))}
            className="text-start"
          >
            <Card className={cn('hover:-translate-y-0.5 transition-all duration-200', statusFilter === filter && 'ring-2 ring-primary')}>
              <CardContent className="flex items-center gap-3 p-4">
                <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br shadow-md', fg, gradient, glow)}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xl font-bold tabular-nums">{isLoading ? '—' : count}</p>
                  <p className="text-xs text-muted-foreground">{label}</p>
                </div>
              </CardContent>
            </Card>
          </button>
        ))}
      </div>

      {/* Table */}
      <Card className="animate-fade-in-up stagger-2">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">
              {statusFilter === 'ACTION_REQUIRED' ? t('actionRequiredHeading') : t('recentSubmissions')}
            </CardTitle>
            <div className="flex items-center gap-1">
              {statusFilter && (
                <Button variant="ghost" size="sm" onClick={() => setStatusFilter('')} className="h-7 text-xs text-muted-foreground">
                  {t('clearFilterBtn')}
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => refetch()} className="h-7 text-xs text-muted-foreground">
                <RefreshCw className="me-1.5 h-3 w-3" />
                {t('refresh')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="divide-y">
              {Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}
            </div>
          ) : !submissions.length ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-muted/60">
                <Receipt className="h-8 w-8 text-muted-foreground/40" />
              </div>
              <p className="text-sm font-semibold text-muted-foreground">{t('noSubmissions')}</p>
              <p className="mt-1 text-xs text-muted-foreground/60 max-w-48">
                {t('noSubmissionsInfo')}
              </p>
            </div>
          ) : (
            <div className="overflow-auto">
              <Table mobileCards>
                <THead>
                  <Tr hover={false}>
                    <Th>{t('invoiceCol')}</Th>
                    <Th className="hidden md:table-cell">{t('kindCol')}</Th>
                    <Th className="hidden sm:table-cell">{t('fbrInvoiceIdCol')}</Th>
                    <Th className="text-center w-28">{t('status')}</Th>
                    <Th className="text-center w-20 hidden md:table-cell">{t('attemptsCol')}</Th>
                    <Th className="w-36 hidden lg:table-cell">{t('submittedCol')}</Th>
                    <Th className="w-10" />
                  </Tr>
                </THead>
                <TBody>
                  {submissions.map((sub) => {
                    const hasSuggestions = sub.fieldSuggestions && Array.isArray(sub.fieldSuggestions) && sub.fieldSuggestions.length > 0;
                    const isActionable = ['REJECTED', 'FAILED', 'INVALID'].includes(sub.status);
                    const isExpanded = expandedId === sub.id;
                    const windowLabel = cancelWindowLabel(sub, t);
                    return (
                      <>
                        <Tr
                          key={sub.id}
                          hover={false}
                          className={cn(
                            'duration-150',
                            isActionable ? 'cursor-pointer hover:bg-accent/40' : '',
                            isExpanded ? 'bg-accent/20' : '',
                          )}
                          onClick={() => isActionable && setExpandedId(isExpanded ? null : sub.id)}
                        >
                          <Td label={t('invoiceCol')} className="py-3.5">
                            <div className="font-mono text-sm font-semibold">
                              {sub.sale?.invoiceNumber || sub.invoiceNumber ? (
                                <RecordLink type="invoice" id={sub.saleId} label={(sub.sale?.invoiceNumber || sub.invoiceNumber) as string} variant="mono" className="text-sm font-semibold" />
                              ) : (
                                sub.saleId
                              )}
                            </div>
                            {windowLabel && (
                              <p className="mt-0.5 text-xs text-muted-foreground">{windowLabel}</p>
                            )}
                          </Td>
                          <Td label={t('kindCol')} className="py-3.5 text-xs text-muted-foreground hidden md:table-cell">
                            {kindDisplayLabel(sub.kind ?? 'SALE_INVOICE', t)}
                          </Td>
                          <Td label={t('fbrInvoiceIdCol')} className="py-3.5 font-mono text-xs text-muted-foreground hidden sm:table-cell">
                            {sub.fbrInvoiceId || '—'}
                          </Td>
                          <Td label={t('status')} className="py-3.5 text-center">
                            <StatusBadge status={isOfflineDummyInvoiceId(sub.fbrInvoiceId) ? 'OFFLINE' : sub.status} />
                          </Td>
                          <Td label={t('attemptsCol')} className="py-3.5 text-center font-mono text-xs text-muted-foreground hidden md:table-cell">
                            {sub.attempts}
                          </Td>
                          <Td label={t('submittedCol')} className="py-3.5 text-xs text-muted-foreground hidden lg:table-cell">
                            {formatDate(sub.createdAt)}
                          </Td>
                          <Td className="py-3.5 text-center">
                            {isActionable && (
                              <ChevronRight className={cn('h-4 w-4 text-muted-foreground transition-transform duration-200', isExpanded && 'rotate-90')} />
                            )}
                          </Td>
                        </Tr>
                        {isExpanded && (
                          <Tr key={`${sub.id}-detail`} hover={false} className="no-card bg-muted/10">
                            <Td colSpan={7} className="py-4">
                              {sub.errorMessage && (
                                <div className="mb-3 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive-text">
                                  {(() => {
                                    const guide = getFbrErrorGuide(sub.errorMessage);
                                    return guide ? (
                                      <>
                                        <p className="font-semibold mb-1 flex items-center gap-1.5">
                                          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                          {guide.title}
                                        </p>
                                        <p className="mb-1.5 text-destructive-text">{guide.explain}</p>
                                        <div className="rounded bg-destructive/15 px-2.5 py-2 border border-destructive/30">
                                          <p className="font-semibold text-destructive-text mb-0.5">How to fix:</p>
                                          <p>{guide.fix}</p>
                                        </div>
                                        <details className="mt-1.5">
                                          <summary className="cursor-pointer text-destructive-text text-[10px]">Show raw FBR error</summary>
                                          <p className="mt-1 font-mono break-all text-[10px] opacity-70">{sub.errorMessage}</p>
                                        </details>
                                      </>
                                    ) : (
                                      <>
                                        <p className="font-semibold mb-1">
                                          {t('fbrError')}
                                        </p>
                                        <p className="font-mono break-all">{sub.errorMessage}</p>
                                      </>
                                    );
                                  })()}
                                </div>
                              )}
                              {hasSuggestions ? (
                                <div>
                                  <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
                                    <AlertTriangle className="h-3.5 w-3.5 text-warning-text" />
                                    {t('fieldSuggestionsTitle')}
                                  </p>
                                  <div className="space-y-2 mb-3">
                                    {(sub.fieldSuggestions ?? []).map((sug, idx) => (
                                      <div key={idx} className="rounded-lg border bg-card px-3 py-2.5 text-xs">
                                        <div className="flex items-start justify-between gap-2">
                                          <div>
                                            <p className="font-semibold text-foreground">
                                              {sug.field}
                                              {sug.itemSku && <span className="ms-1 font-mono text-muted-foreground">(SKU: {sug.itemSku})</span>}
                                            </p>
                                            <p className="mt-0.5 text-muted-foreground">{sug.message}</p>
                                          </div>
                                          {sug.suggestedValue !== undefined && (
                                            <span className="shrink-0 rounded bg-success/15 px-2 py-1 font-mono text-success-text">
                                              → {String(sug.suggestedValue)}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  {canApplyFix && (
                                    <button
                                      disabled={applyFixMutation.isPending}
                                      onClick={(e) => { e.stopPropagation(); applyFixMutation.mutate(sub.id); }}
                                      className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                                    >
                                      {applyFixMutation.isPending ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                      ) : (
                                        <CheckCircle className="h-3 w-3" />
                                      )}
                                      {t('applyFixes')}
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <p className="text-xs text-muted-foreground italic">
                                  {t('noFieldSuggestions')}
                                </p>
                              )}
                            </Td>
                          </Tr>
                        )}
                      </>
                    );
                  })}
                </TBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── API Logs Tab ─────────────────────────────────────────────────────────────

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

interface FbrApiLog {
  id: string;
  context: string;
  contextId: string | null;
  /** Human invoice number of the linked sale (null when unlinked). */
  invoiceNumber?: string | null;
  /** POS-I239: the sale behind invoiceNumber, so the log row opens it in place. */
  saleId?: string | null;
  environment: string;
  url: string;
  method: string;
  requestHeaders: Record<string, string> | null;
  requestBody: JsonValue;
  responseStatus: number | null;
  responseBody: JsonValue;
  responseHeaders: Record<string, string> | null;
  durationMs: number | null;
  isTest: boolean;
  createdAt: string;
}

function ApiLogsTab() {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filterEnv, setFilterEnv] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1);
  const { t } = useTranslation('fbr');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['fbr-api-logs', page, filterEnv, filterStatus],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '25' });
      if (filterEnv) params.set('environment', filterEnv);
      if (filterStatus) params.set('responseStatus', filterStatus);
      return api.get(`/fbr/logs?${params}`).then((r) => r.data);
    },
  });

  const { data: detailData, isLoading: isDetailLoading } = useQuery({
    queryKey: ['fbr-api-log-detail', expandedId],
    queryFn: () => api.get(`/fbr/logs/${expandedId}`).then((r) => r.data),
    enabled: expandedId !== null,
  });

  const logs: FbrApiLog[] = data?.data ?? [];
  const total: number = data?.total ?? 0;
  const totalPages = Math.ceil(total / 25);

  const getStatusColor = (status: number | null) => {
    if (!status) return 'text-muted-foreground';
    if (status >= 200 && status < 300) return 'text-success-text';
    if (status >= 400 && status < 500) return 'text-warning-text';
    return 'text-destructive-text';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-2">
          <Dropdown
            value={filterEnv}
            onChange={(e) => { setFilterEnv(e.value); setPage(1); }}
            options={[
              { label: t('allEnvironments'), value: '' },
              { label: 'OFF', value: 'OFF' },
              { label: 'STAGING', value: 'STAGING' },
              { label: 'LIVE', value: 'LIVE' },
            ]}
            optionLabel="label"
            optionValue="value"
            placeholder={t('allEnvironments')}
          />
          <Dropdown
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.value); setPage(1); }}
            options={[
              { label: t('allStatuses'), value: '' },
              { label: '200 OK', value: '200' },
              { label: '201 Created', value: '201' },
              { label: '400 Bad Request', value: '400' },
              { label: '401 Unauthorized', value: '401' },
              { label: '403 Forbidden', value: '403' },
              { label: '500 Server Error', value: '500' },
            ]}
            optionLabel="label"
            optionValue="value"
            placeholder={t('allStatuses')}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{t('logsCount', { count: total })}</span>
          <Button variant="outline" size="sm" onClick={() => refetch()} className="h-8 text-xs">
            <RefreshCw className="me-1.5 h-3 w-3" />
            {t('refresh')}
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="divide-y">
            {Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Activity className="h-10 w-10 text-muted-foreground/40 mb-3" />
            <p className="text-sm font-medium text-muted-foreground">{t('noApiLogs')}</p>
            <p className="text-xs text-muted-foreground/60 mt-1">{t('logsAppearInfo')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  <Th>{t('timeCol')}</Th>
                  <Th>{t('contextCol')}</Th>
                  <Th>{t('environmentCol')}</Th>
                  <Th>{t('status')}</Th>
                  <Th>{t('durationCol')}</Th>
                  <Th />
                </Tr>
              </THead>
              <TBody>
                {logs.map((log) => (
                  <Fragment key={log.id}>
                    <Tr
                      key={log.id}
                      className="cursor-pointer"
                      onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                    >
                      <Td label={t('timeCol')} className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(log.createdAt)}
                      </Td>
                      <Td label={t('contextCol')}>
                        {log.invoiceNumber ? (
                          <span className="rounded bg-muted px-1.5 py-0.5" title={log.context}>
                            <RecordLink type="invoice" id={log.saleId} label={log.invoiceNumber} variant="mono" className="text-xs" />
                          </span>
                        ) : (
                          <>
                            <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                              {log.context}
                            </span>
                            {log.contextId && (
                              <span className="ms-1.5 text-xs text-muted-foreground">
                                {log.contextId.slice(0, 8)}…
                              </span>
                            )}
                          </>
                        )}
                      </Td>
                      <Td label={t('environmentCol')}>
                        <span className={cn(
                          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
                          log.environment === 'STAGING' && 'bg-warning/10 text-warning-text ring-warning/30',
                          log.environment === 'LIVE' && 'bg-success/10 text-success-text ring-success/30',
                          log.environment === 'OFF' && 'bg-muted text-muted-foreground ring-border',
                        )}>
                          {log.environment}
                        </span>
                      </Td>
                      <Td label={t('status')}>
                        <span className={cn('font-mono text-xs font-semibold', getStatusColor(log.responseStatus))}>
                          {log.responseStatus ?? '—'}
                        </span>
                      </Td>
                      <Td label={t('durationCol')} className="text-xs text-muted-foreground">
                        {log.durationMs != null ? `${log.durationMs}ms` : '—'}
                      </Td>
                      <Td>
                        <ChevronRight className={cn(
                          'h-4 w-4 text-muted-foreground transition-transform',
                          expandedId === log.id && 'rotate-90',
                        )} />
                      </Td>
                    </Tr>

                    {expandedId === log.id && (
                      <Tr key={`${log.id}-detail`} hover={false} className="no-card bg-muted/20">
                        <Td colSpan={6} className="py-4">
                          {isDetailLoading ? (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              {t('loadingDetailsLabel')}
                            </div>
                          ) : detailData ? (
                            <div className="space-y-3">
                              {/* URL + Method */}
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground mb-1">{t('requestLabel')}</p>
                                <p className="font-mono text-xs break-all text-foreground">
                                  <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-bold bg-muted text-foreground me-2">
                                    {detailData.method ?? 'POST'}
                                  </span>
                                  {detailData.url}
                                </p>
                              </div>

                              {/* Request Headers */}
                              {detailData.requestHeaders && (
                                <div>
                                  <p className="text-xs font-semibold text-muted-foreground mb-1">{t('requestHeadersLabel')}</p>
                                  <pre className="text-xs bg-code-surface text-code-surface-foreground rounded-lg p-3 overflow-x-auto max-h-40">
                                    {JSON.stringify(detailData.requestHeaders, null, 2)}
                                  </pre>
                                </div>
                              )}

                              {/* Request Body */}
                              {detailData.requestBody && (
                                <div>
                                  <p className="text-xs font-semibold text-muted-foreground mb-1">{t('requestBodyLabel')}</p>
                                  <pre className="text-xs bg-code-surface text-code-surface-foreground rounded-lg p-3 overflow-x-auto max-h-60">
                                    {JSON.stringify(detailData.requestBody, null, 2)}
                                  </pre>
                                </div>
                              )}

                              {/* Response Body */}
                              {detailData.responseBody && (
                                <div>
                                  <p className="text-xs font-semibold text-muted-foreground mb-1">
                                    {t('responseBodyLabel')}
                                    <span className={cn('ms-2 font-mono', getStatusColor(detailData.responseStatus))}>
                                      ({detailData.responseStatus})
                                    </span>
                                  </p>
                                  <pre className="text-xs bg-code-surface text-code-surface-foreground rounded-lg p-3 overflow-x-auto max-h-60">
                                    {JSON.stringify(detailData.responseBody, null, 2)}
                                  </pre>
                                </div>
                              )}

                              {/* Response Headers */}
                              {detailData.responseHeaders && (
                                <div>
                                  <p className="text-xs font-semibold text-muted-foreground mb-1">{t('responseHeadersLabel')}</p>
                                  <pre className="text-xs bg-code-surface text-code-surface-foreground rounded-lg p-3 overflow-x-auto max-h-40">
                                    {JSON.stringify(detailData.responseHeaders, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground py-2">{t('failedToLoadDetails')}</p>
                          )}
                        </Td>
                      </Tr>
                    )}
                  </Fragment>
                ))}
              </TBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>{t('previousBtn')}</Button>
          <span className="flex items-center text-xs text-muted-foreground">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>{t('nextBtn')}</Button>
        </div>
      )}
    </div>
  );
}

// ─── Products Tab ─────────────────────────────────────────────────────────────

interface ProductFbrStatus {
  id: string;
  sku: string;
  name: string;
  hsCode: string | null;
  uom: string | null;
  fbrReady: boolean;
  fbrIssue: string | null;
}

interface ProductFbrPage {
  data: ProductFbrStatus[];
  total: number;
  page: number;
  limit: number;
}

type FbrFilter = 'all' | 'valid' | 'invalid' | 'unvalidated';

const FBR_UOMS = [
  // Confirmed accepted by FBR DI API (from architecture docs + live testing)
  'Pcs', 'KG', 'Gram', 'MT', 'Liter', 'Meter', 'KWH', 'MMBTU',
  'Bag', 'SET', 'Dozen', 'Pair', 'Barrels', 'Packs',
  'Square Metre', 'Square Foot',
  'Numbers, pieces, units', 'Thousand Unit',
  // Also accepted (from FBR reference API)
  'KGs', 'Grams', 'Liters', 'Meters', 'Dozens', 'Pairs', 'Sets', 'Pieces', 'Bags',
  'Bottles', 'Boxes', 'Tablets', 'Cans/Tins', 'Metric Ton', 'Numbers',
  'Others',
];

interface FbrEditForm {
  hsCode: string;
  uom: string;
}

function ProductsTab() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('fbr');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FbrFilter>('all');
  const [editProduct, setEditProduct] = useState<ProductFbrStatus | null>(null);
  const [editForm, setEditForm] = useState<FbrEditForm>({ hsCode: '', uom: '' });
  const limit = 20;

  const { data, isLoading } = useQuery<ProductFbrPage>({
    queryKey: ['fbr-products', page, search, filter],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        ...(search ? { search } : {}),
        ...(filter !== 'all' ? { fbrFilter: filter } : {}),
      });
      return api.get(`/fbr/products?${params}`).then(r => r.data);
    },
  });

  const saveFbrFieldsMutation = useMutation({
    mutationFn: (vars: { productId: string; form: FbrEditForm }) =>
      api.patch(`/products/${vars.productId}`, {
        hsCode: vars.form.hsCode || undefined,
        uom: vars.form.uom || undefined,
      }).then(r => r.data),
    onSuccess: () => {
      toast.success(t('fbrFieldsSaved'));
      setEditProduct(null);
      queryClient.invalidateQueries({ queryKey: ['fbr-products'] });
    },
    onError: (err) => notify.error(err, t('saveFailed')),
  });

  const handleOpenEdit = (product: ProductFbrStatus) => {
    setEditProduct(product);
    setEditForm({
      hsCode: product.hsCode ?? '',
      uom: product.uom ?? '',
    });
  };

  const products = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const readyCount = products.filter(p => p.fbrReady).length;
  const issueCount = products.filter(p => !p.fbrReady && p.hsCode).length;
  const noHsCount = products.filter(p => !p.hsCode).length;

  const FILTER_OPTIONS: { value: FbrFilter; label: string; icon: typeof CheckCircle; cls: string }[] = [
    { value: 'all', label: t('allFilter'), icon: Filter, cls: '' },
    { value: 'valid', label: t('fbrReadyFilter'), icon: CheckCircle, cls: 'text-success-text' },
    { value: 'invalid', label: t('issuesFilter'), icon: XCircle, cls: 'text-destructive-text' },
    { value: 'unvalidated', label: t('noHsFilter'), icon: Clock, cls: 'text-warning-text' },
  ];

  return (
    <div className="space-y-4">
      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border bg-success/10 p-3 flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-success-text shrink-0" />
          <div>
          <p className="text-xs text-muted-foreground">{t('fbrReadyStat')}</p>
            <p className="text-xl font-bold text-success-text">{data ? readyCount : '—'}</p>
          </div>
        </div>
        <div className="rounded-lg border bg-destructive/10 p-3 flex items-center gap-3">
          <XCircle className="h-5 w-5 text-destructive-text shrink-0" />
          <div>
          <p className="text-xs text-muted-foreground">{t('uomIssueStat')}</p>
            <p className="text-xl font-bold text-destructive-text">{data ? issueCount : '—'}</p>
          </div>
        </div>
        <div className="rounded-lg border bg-warning/10 p-3 flex items-center gap-3">
          <Clock className="h-5 w-5 text-warning-text shrink-0" />
          <div>
          <p className="text-xs text-muted-foreground">{t('noHsCodeStat')}</p>
            <p className="text-xl font-bold text-warning-text">{data ? noHsCount : '—'}</p>
          </div>
        </div>
      </div>

      {/* Search + Filter bar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder={t('searchByNameOrSku')}
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="ps-9 pe-3"
          />
        </div>
        <div className="flex gap-1">
          {FILTER_OPTIONS.map(({ value, label, icon: Icon, cls }) => (
            <button
              key={value}
              onClick={() => { setFilter(value); setPage(1); }}
              className={cn(
                'flex items-center gap-1.5 rounded-md border px-3 py-2 text-xs font-medium transition-all',
                filter === value
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground hover:text-foreground hover:bg-muted',
              )}
            >
              <Icon className={cn('h-3.5 w-3.5', filter === value ? '' : cls)} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Products table */}
      <Card>
        <div className="overflow-x-auto">
          <Table mobileCards>
            <THead>
              <Tr hover={false}>
                <Th>{t('productCol')}</Th>
                <Th>{t('hsCodeCol')}</Th>
                <Th>{t('uomCol')}</Th>
                <Th>{t('fbrStatusCol')}</Th>
                <Th className="text-end">{t('actionCol')}</Th>
              </Tr>
            </THead>
            <TBody>
              {isLoading ? (
                <Tr hover={false} className="no-card">
                  <Td colSpan={5} className="py-12 text-center text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                    {t('loadingProducts')}
                  </Td>
                </Tr>
              ) : products.length === 0 ? (
                <Tr hover={false} className="no-card">
                  <Td colSpan={5} className="py-12 text-center text-muted-foreground">
                    <Package className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p>{t('noProductsFound')}</p>
                  </Td>
                </Tr>
              ) : (
                products.map(product => {
                  return (
                    <Tr key={product.id}>
                      <Td label={t('productCol')}>
                        <div className="font-medium">{product.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{product.sku}</div>
                      </Td>
                      <Td label={t('hsCodeCol')} className="font-mono text-xs">
                        {product.hsCode || <span className="text-muted-foreground italic">—</span>}
                      </Td>
                      <Td label={t('uomCol')} className="text-xs font-mono">
                        {product.uom || <span className="text-muted-foreground">—</span>}
                      </Td>
                      <Td label={t('fbrStatusCol')}>
                        {product.fbrReady ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-success/10 text-success-text border border-success/30 px-2 py-0.5 text-xs font-medium">
                            <CheckCircle className="h-3 w-3" /> {t('readyBadge')}
                          </span>
                        ) : product.fbrIssue ? (
                          <div>
                            <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 text-destructive-text border border-destructive/30 px-2 py-0.5 text-xs font-medium">
                              <XCircle className="h-3 w-3" /> {t('issueBadge')}
                            </span>
                            <p className="text-xs text-destructive-text mt-1 max-w-[200px] truncate" title={product.fbrIssue}>
                              {product.fbrIssue}
                            </p>
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 text-warning-text border border-warning/30 px-2 py-0.5 text-xs font-medium">
                            <Clock className="h-3 w-3" /> {t('noHsCodeBadge')}
                          </span>
                        )}
                      </Td>
                      <Td label={t('actionCol')} className="text-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenEdit(product)}
                          className="text-xs h-7 px-2"
                          title={t('editFbrFields')}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                      </Td>
                    </Tr>
                  );
                })
              )}
            </TBody>
          </Table>
        </div>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>{t('previousBtn')}</Button>
          <span className="flex items-center text-xs text-muted-foreground px-2">{page} / {totalPages} ({total} total)</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>{t('nextBtn')}</Button>
        </div>
      )}

      {/* ── Edit FBR Fields Dialog ── */}
      <Dialog
        visible={!!editProduct}
        onHide={() => setEditProduct(null)}
        header={
          editProduct ? (
            <div>
              <div className="font-semibold">{t('editFbrFields')}</div>
              <div className="text-xs font-normal text-muted-foreground mt-0.5">{editProduct.name} · {editProduct.sku}</div>
            </div>
          ) : null
        }
        style={{ width: '32rem' }}
        draggable={false}
        resizable={false}
        footer={
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => setEditProduct(null)}>{t('cancelBtn')}</Button>
            <Button
              size="sm"
              onClick={() => editProduct && saveFbrFieldsMutation.mutate({ productId: editProduct.id, form: editForm })}
              disabled={saveFbrFieldsMutation.isPending}
              className="gap-1"
            >
              {saveFbrFieldsMutation.isPending ? (
                <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('savingLabel')}</>
              ) : (
                <><Save className="h-3.5 w-3.5" /> {t('saveBtn')}</>
              )}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 py-1">
          {/* HS Code */}
          <div>
            <Label className="block mb-1.5">{t('hsCodeFieldLabel')} <span className="text-muted-foreground font-normal text-xs">(PCT code, e.g. 3305.1000)</span></Label>
            <Input
              type="text"
              value={editForm.hsCode}
              onChange={e => setEditForm(f => ({ ...f, hsCode: e.target.value }))}
              placeholder="e.g. 3305.1000"
              className="font-mono"
            />
          </div>

          {/* UOM */}
          <div>
            <Label className="block mb-1.5">{t('uomFieldLabel')}</Label>
            <Dropdown
              value={editForm.uom}
              onChange={e => setEditForm(f => ({ ...f, uom: e.value }))}
              options={[{ label: t('selectUomPlaceholder'), value: '' }, ...FBR_UOMS.map(u => ({ label: u, value: u }))]}
              optionLabel="label"
              optionValue="value"
              placeholder={t('selectUomPlaceholder')}
              className="w-full"
            />
          </div>

          <p className="text-xs text-muted-foreground">
            {t('sroAutoConfigNote')}
          </p>
        </div>
      </Dialog>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const TABS: { id: Tab; label: string; icon: typeof Settings; description: string }[] = [
  { id: 'settings', label: 'Settings', icon: Settings, description: 'Configure token & mode' },
  { id: 'scenarios', label: 'Scenario Checklist', icon: ShieldCheck, description: 'Create & post FBR scenario invoices' },
  { id: 'products', label: 'Products', icon: Package, description: 'FBR validation status per product' },
  { id: 'submissions', label: 'Submissions', icon: Receipt, description: 'View submission history' },
  { id: 'api-logs', label: 'API Logs', icon: Activity, description: 'Full request/response logs for every FBR API call' },
];

export function FbrPage() {
  const [tab, setTab] = useState<Tab>('settings');
  const { t } = useTranslation('fbr');

  const TAB_LABEL_KEYS: Record<Tab, string> = {
    settings: t('tabSettings'),
    scenarios: t('tabScenarios'),
    products: t('tabProducts'),
    submissions: t('tabSubmissions'),
    'api-logs': t('tabApiLogs'),
  };

  const { data: settings } = useQuery<FbrSettings>({
    queryKey: ['fbr-settings'],
    queryFn: async () => api.get('/tenant/fbr-settings').then((r) => r.data),
  });

  const currentMode = settings?.fbrEnvironment ?? 'OFF';
  const modeMeta = MODE_META[currentMode];
  const ModeIcon = modeMeta.icon;

  return (
    <div className="space-y-6 page-enter">
      {/* ── Page header ── */}
      <PageHeader
        title={t('pageTitle')}
        subtitle={t('pageSubtitle')}
        actions={
          <div className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset transition-all duration-300',
            modeMeta.bg, modeMeta.text, modeMeta.ring,
          )}>
            <ModeIcon className="h-3 w-3" />
            {modeMeta.label}
          </div>
        }
      />

      {/* ── Tabs ── */}
      <div className="animate-fade-in-up -mx-1 border-b border-border/60 pb-1">
        <TabBar
          tabs={TABS.map(tabItem => ({ key: tabItem.id, label: TAB_LABEL_KEYS[tabItem.id], icon: tabItem.icon }))}
          activeTab={tab}
          onTabChange={(key) => setTab(key as Tab)}
        />
      </div>

      {/* ── Tab content ── */}
      <div key={tab} className="animate-fade-in-up">
        {tab === 'settings' && <SettingsTab />}
        {tab === 'scenarios' && <ScenarioChecklistTab />}
        {tab === 'products' && <ProductsTab />}
        {tab === 'submissions' && <SubmissionsTab />}
        {tab === 'api-logs' && <ApiLogsTab />}
      </div>
    </div>
  );
}

